import React from "react";
import _ from "lodash";

import { getInitials, getDataGridRandomAvatarColor } from "Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getMomentFromDateAndTimeString,
  checkIsDueDateBeforeOpenDate,
  checkIsCloseDateBeforeDueDate,
  checkIsCloseDateBeforeOpenDate,
  checkIsCloseDateBeforeCurrentDate,
  getOpenDateRange,
  getDueDateRange,
  getCloseDateRange,
  getOpensOnBasedOnTimelineSettings,
  getDueOnBasedOnTimelineSettings,
  getClosesOnBasedOnTimelineSettings,
  getAssignmentVisibilityOptions,
  getTagOptions,
} from "../../Utils";
import { getScheduleOrDeadlineTime } from "ClassRoom/modules/utils";
import moment from "moment";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { getUserArchivalStatus } from "modules/CommonSelectors";

import {
  OPENS_ON,
  DUE_ON,
  CLOSES_ON,
  VISIBILITY,
  STUDENTS,
  TAGS,
  SECURITY_MODE,
  DURATION,
} from "Constants/stringConstants";
import {
  UNRESTRICTED,
  BROWSER_LOCK,
  LOCKDOWN_BROWSER,
} from "Worksheet/modules/WorksheetConstants";
import {
  LockOpenOutlined,
  LockOutlined,
  PolicyOutlined,
} from "@toddle-edu/ds-icons";
import { checkIsDateBefore } from "Utils/DateTimeHelpers";

// * Security mode options for worksheet assignment settings
const getWorksheetSecurityModeOptions = t => [
  {
    id: UNRESTRICTED,
    value: UNRESTRICTED,
    label: t("worksheet:no_restriction"),
    prefix: {
      type: "icon",
      attrs: { name: "LockOpenOutlined", icon: <LockOpenOutlined /> },
    },
  },
  {
    id: BROWSER_LOCK,
    value: BROWSER_LOCK,
    label: t("worksheet:browser_lock"),
    prefix: {
      type: "icon",
      attrs: { name: "LockOutlined", icon: <LockOutlined /> },
    },
  },
  {
    id: LOCKDOWN_BROWSER,
    value: LOCKDOWN_BROWSER,
    label: t("worksheet:lockdown_application"),
    prefix: {
      type: "icon",
      attrs: { name: "PolicyOutlined", icon: <PolicyOutlined /> },
    },
  },
];

export const getCourseStudentsFromUserMap = ({ coursesUserMap, courseId }) => {
  const courseData = _.get(coursesUserMap, courseId);
  const userMap = _.get(courseData, "userMap", []);
  return _.map(userMap?.edges, "node.user");
};

const shouldDisableClassRemoveButton = ({
  id,
  courseId,
  totalSelectedClasses,
  isDuplicateAssignment,
  doesStaffHaveAccessToCourse,
  mode,
  isIncludedInCourseFlow,
}) => {
  const defaultCondition = courseId === id && !isDuplicateAssignment;

  if (!getIsLearningCoursesEnabled()) {
    return defaultCondition;
  }

  switch (mode) {
    case "edit":
      return (
        (totalSelectedClasses === 1 && !isIncludedInCourseFlow) ||
        !doesStaffHaveAccessToCourse
      );
    default:
      return defaultCondition && !isIncludedInCourseFlow;
  }
};

const getFormattedStudentForDropdown = student => {
  const id = _.get(student, "id");
  const label = getFormattedUserName({ user: student });

  return {
    id,
    label,
    prefix: {
      type: "avatar",
      attrs: {
        name: getInitials({ value: label }),
        color: getDataGridRandomAvatarColor(id),
      },
    },
    isArchived: getUserArchivalStatus(student),
  };
};

const getStudentOptions = students => {
  return _.map(students, student => getFormattedStudentForDropdown(student));
};

const getStudentsListFromUserMap = ({
  coursesUserMap,
  assignedStudents,
  isExistingClass,
  courseId,
}) => {
  const courseUserMap = _.get(coursesUserMap, courseId)?.userMap?.edges || [];

  let studentsList = _.map(courseUserMap, item => {
    return {
      ..._.get(item, "node.user", {}),
      tags: _.get(item, "node.tags.length")
        ? _.get(item, "node.tags")
        : _.map(_.get(item, "node.subjectWiseLevel", []), "level"),
    };
  });

  if (isExistingClass) {
    const assignedStudentsList = _.map(assignedStudents, student => {
      return {
        ...student,
        tags: [],
      };
    });
    studentsList = _.uniqBy([...studentsList, ...assignedStudentsList], "id");
  }

  return _.sortBy(studentsList, student => getUserArchivalStatus(student));
};

const getStudentOptionsToDisplay = ({ allStudents, t }) => {
  if (_.isEmpty(allStudents)) {
    return [];
  }

  const groupedStudents = _.groupBy(allStudents, student =>
    _.get(student, "tags.0.label")
  );

  const shouldGroupStudents =
    _.every(allStudents, student => !_.isEmpty(student?.tags)) &&
    _.size(groupedStudents) > 1;

  if (shouldGroupStudents) {
    return _.map(groupedStudents, (students, label) => ({
      value: label,
      label: t("common:all_students_with_label", {
        label,
      }),
      options: getStudentOptions(students),
    }));
  }

  return getStudentOptions(allStudents);
};

const getDueDateTooltip = ({ isDueDateBeforeOpenDate, t }) => {
  if (isDueDateBeforeOpenDate) {
    return t("classRoom:due_date_cannot_be_before_open_date");
  }
  return undefined;
};

const getCloseDateTooltip = ({
  isCloseDateBeforeDueDate,
  isCloseDateBeforeCurrentDate,
  isCloseDateBeforeOpenDate,
  t,
}) => {
  if (isCloseDateBeforeDueDate) {
    return t("classRoom:close_date_cannot_be_before_due_date");
  }
  if (isCloseDateBeforeCurrentDate) {
    return t("classRoom:close_time_cannot_be_in_the_past");
  }
  if (isCloseDateBeforeOpenDate) {
    return t("classRoom:close_date_cannot_be_before_open_date");
  }
  return undefined;
};

// Helper function to transform courses data for DataGrid format
export const transformCoursesToGridData = params => {
  const {
    courses,
    coursesUserMap,
    academicYearSelected,
    allowBackDate,
    selectedTimeSlot,
    isDraftMode,
    currentTimelineSettings,
    dueTimePreference,
    assignmentData,
    mode,
    isSubmissionPendingForAll,
    isStudentSubmissionEnabled,
    filteredStaffCoursesIds,
    isFixedStartTime,
    genericFolders,
    courseSelectedFoldersMap,
    courseId,
    isDuplicateAssignment,
    isIncludedInCourseFlow,
    handleDeleteIconClick,
    assignmentsState,
    classWiseSettingsToDisplay,
    opensOnPreference,
    dueOnPreference,
    closesOnPreference,
    securityMode,
    timedTestDuration,
    t,
  } = params;

  const oldCourses = _.get(assignmentData, "courses", []);
  const totalSelectedClasses = _.size(courses);

  return _.map(courses, (course, index) => {
    const {
      id,
      title,
      students,
      deadlineDate,
      deadlineTime,
      closeSubmissionDate,
      state = {},
      visibility,
      // Per-class worksheet settings (when WorksheetAssignModalV2 flag is ON)
      isLockScreenEnabled: courseIsLockScreenEnabled,
      isSafeBrowserEnabled: courseIsSafeBrowserEnabled,
      timedTestDuration: courseTimedTestDuration,
    } = course;

    // When per-class values have been explicitly set, derive security mode from them - otherwise fall back to global values passed via params
    const hasPerClassSecurityMode =
      !_.isUndefined(courseIsSafeBrowserEnabled) ||
      !_.isUndefined(courseIsLockScreenEnabled);
    const effectiveSecurityMode = hasPerClassSecurityMode
      ? courseIsSafeBrowserEnabled
        ? LOCKDOWN_BROWSER
        : courseIsLockScreenEnabled
        ? BROWSER_LOCK
        : UNRESTRICTED
      : securityMode;
    const effectiveTimedTestDuration =
      courseTimedTestDuration || timedTestDuration;

    const { scheduleDate, scheduleTime } = state;

    const oldCourseData = _.find(oldCourses, { id }) || {};
    const isExistingClass =
      mode === "edit" &&
      !_.isEmpty(assignmentData) &&
      !_.isEmpty(oldCourseData);

    const preventFutureOpenDate =
      mode === "edit" &&
      assignmentsState === "PUBLISHED" &&
      index === 0 &&
      !isSubmissionPendingForAll;

    const {
      state: {
        scheduleDate: oldScheduleDate,
        scheduleTime: oldScheduleTime,
      } = {},
      closeSubmissionDate: previousCloseSubmissionDate,
    } = oldCourseData || {};

    // In new course setup, disable inputs and delete button if staff does not have
    // manage assignment/gradebook access to the course(class)
    const doesStaffHaveAccessToCourse =
      !getIsLearningCoursesEnabled() || _.includes(filteredStaffCoursesIds, id);

    // Check if course data is loading by seeing if it exists in coursesUserMap
    const isStudentsLoading = !_.get(coursesUserMap, id);

    const allStudents = getStudentsListFromUserMap({
      coursesUserMap,
      assignedStudents: students,
      isExistingClass,
      courseId: id,
    });

    const studentOptions = getStudentOptionsToDisplay({
      allStudents,
      t,
    });

    const { time: initialTime } = getScheduleOrDeadlineTime({
      date: moment(),
    });
    const initialTimeMoment = getMomentFromDateAndTimeString({
      timeString: initialTime,
    });

    const opensOn = getMomentFromDateAndTimeString({
      dateString: scheduleDate,
      timeString: scheduleTime,
    });

    const previousOpensOn = getMomentFromDateAndTimeString({
      dateString: oldScheduleDate,
      timeString: oldScheduleTime,
    });

    const dueOn = getMomentFromDateAndTimeString({
      dateString: deadlineDate,
      timeString: deadlineTime,
    });

    const closesOn = getMomentFromDateAndTimeString({
      dateTimeString: closeSubmissionDate,
    });

    const previousClosesOn = getMomentFromDateAndTimeString({
      dateTimeString: previousCloseSubmissionDate,
    });

    // Calculate dates based on timeline settings (similar to original)
    const dueOnToConsider = getDueOnBasedOnTimelineSettings({
      dueOn,
      currentTimelineSettings,
    });
    const isDueDateInPast = checkIsDateBefore({
      date1: dueOnToConsider,
      date2: moment(),
      key: "minutes",
    });

    const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
      opensOn,
      previousOpensOn,
      currentTimelineSettings,
      isDueDateInPast,
    });

    const closesOnToConsider = getClosesOnBasedOnTimelineSettings({
      closesOn,
      currentTimelineSettings,
    });

    // Get academic year constraints
    const academicYearStartDate = _.get(
      academicYearSelected,
      "startDate",
      null
    );
    const academicYearEndDate = _.get(academicYearSelected, "endDate", null);

    // Calculate date ranges for validation
    const openDateRange = getOpenDateRange({
      academicYearStartDate,
      academicYearEndDate,
      allowBackDate,
      preventFutureOpenDate,
      initialTimeMoment,
      opensOn,
    });

    const dueDateRange = getDueDateRange({
      academicYearEndDate,
      academicYearStartDate,
      initialTimeMoment,
      opensOn: opensOnToConsider,
      dueOn,
    });

    const closeDateRange = getCloseDateRange({
      academicYearEndDate,
      initialTimeMoment,
      opensOn: opensOnToConsider,
      dueOn: dueOnToConsider,
      closesOn,
    });

    // Validation checks
    const isDueDateBeforeOpenDate = checkIsDueDateBeforeOpenDate({
      dueOn: dueOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });

    const isCloseDateBeforeDueDate = checkIsCloseDateBeforeDueDate({
      closesOn: closesOnToConsider,
      dueOn: dueOnToConsider,
    });

    const isCloseDateBeforeOpenDate = checkIsCloseDateBeforeOpenDate({
      closesOn: closesOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });

    const isCloseDateBeforeCurrentDate = checkIsCloseDateBeforeCurrentDate({
      isDraftMode,
      closesOnToConsider,
      previousClosesOn,
    });

    // Get time preferences
    const dueTimePreferenceMoment = !_.isEmpty(dueTimePreference)
      ? moment(dueTimePreference, "HH:mm a")
      : null;

    const opensOnPreferenceMoment = !_.isEmpty(opensOnPreference)
      ? moment(opensOnPreference, "HH:mm a")
      : null;

    const dueOnPreferenceMoment = !_.isEmpty(dueOnPreference)
      ? moment(dueOnPreference, "HH:mm a")
      : null;

    const closesOnPreferenceMoment = !_.isEmpty(closesOnPreference)
      ? moment(closesOnPreference, "HH:mm a")
      : null;

    const selectedTimeSlotMoment = getMomentFromDateAndTimeString({
      timeString: _.get(selectedTimeSlot, "endTime"),
    });

    const visibilityOptions = getAssignmentVisibilityOptions({
      t,
      opensOn: opensOnToConsider,
      isFixedStartTime,
    });
    const visiblityValue =
      _.find(visibilityOptions, { value: visibility }) ||
      _.first(visibilityOptions);

    const tagOptions = getTagOptions({
      courseId: id,
      genericFolders,
    });

    const disableRemoveButton = shouldDisableClassRemoveButton({
      id,
      courseId,
      totalSelectedClasses,
      isDuplicateAssignment,
      doesStaffHaveAccessToCourse,
      mode,
      isIncludedInCourseFlow,
    });

    const initialColumns = [
      {
        // Add actions column
        value: "MinusCircleOutlined",
        cellType: "icon",
        isEditable: false,
        tooltip:
          disableRemoveButton && getIsLearningCoursesEnabled()
            ? !doesStaffHaveAccessToCourse
              ? t("classRoom:no_access_to_remove_class_from_assignment")
              : t("classRoom:cannot_remove_single_class_if_not_in_course_flow")
            : undefined,
        isDisabled: disableRemoveButton,
        onIconClick: ({ rowId }) => {
          if (!disableRemoveButton) {
            handleDeleteIconClick({ rowId });
          }
        },
      },
      {
        value: title,
        cellType: "label",
        isEditable: false,
        prefix: [
          {
            type: "avatar",
            name: getInitials({ value: title }),
            color: getDataGridRandomAvatarColor(id),
            size: 28,
            shape: "square",
          },
        ],
        styles: {
          selectedColor: "white",
        },
      },
    ];

    const columns = _.reduce(
      classWiseSettingsToDisplay,
      (columns, { key, label }) => {
        switch (key) {
          case STUDENTS:
            // Students - dropdown cell (multi-select) or skeleton when loading
            columns.push(
              isStudentsLoading
                ? {
                    cellType: "skeleton",
                    isEditable: false,
                  }
                : {
                    value: _.map(students, student =>
                      getFormattedStudentForDropdown(student)
                    ),
                    cellType: "dropdown",
                    isEditable: true,
                    isMulti: true,
                    placeholder: t("common:select"),
                    validInputs: {
                      options: studentOptions,
                    },
                    isDisabled: !doesStaffHaveAccessToCourse,
                    addNewOptionEnabled: false,
                    allStudents,
                  }
            );
            break;

          case SECURITY_MODE: {
            const worksheetSecurityOptions = getWorksheetSecurityModeOptions(t);
            // Use per-class security mode when available
            const currentSecurityOption =
              _.find(worksheetSecurityOptions, {
                value: effectiveSecurityMode,
              }) || _.first(worksheetSecurityOptions);
            columns.push({
              value: [currentSecurityOption],
              cellType: "dropdown",
              isEditable: true,
              isMulti: false,
              placeholder: t("worksheet:security_mode"),
              validInputs: {
                options: worksheetSecurityOptions,
              },
              isDisabled: !doesStaffHaveAccessToCourse,
              addNewOptionEnabled: false,
            });
            break;
          }
          case DURATION: {
            // Convert {hours, minutes} to seconds for durationPicker cell
            // Default to 1 hour (3600s) when no duration is set
            const durationInSeconds = effectiveTimedTestDuration
              ? effectiveTimedTestDuration.hours * 3600 +
                effectiveTimedTestDuration.minutes * 60
              : 3600;
            columns.push({
              value: durationInSeconds,
              cellType: "durationPicker",
              isEditable: true,
              placeholder: t("common:duration"),
              isDisabled: !doesStaffHaveAccessToCourse,
              pickerProps: {
                showIcon: true,
                showMinutes: true,
                showSeconds: false,
                minuteStep: 5,
              },
            });
            break;
          }
          case OPENS_ON:
            // Opens on - dateTime cell with validation
            columns.push({
              value: opensOn,
              cellType: "dateTime",
              isEditable: true,
              placeholder: t("common:now_uppercase"),
              isDisabled: !doesStaffHaveAccessToCourse,
              pickerProps: {
                type: "dateTime",
                showIcon: _.isNil(opensOn) ? false : true,
                minDate: openDateRange.minDate,
                maxDate: openDateRange.maxDate,
                minTime: openDateRange.minTime,
                maxTime: openDateRange.maxTime,
                initialTime: opensOnPreferenceMoment,
                isClearable: true,
              },
              customDefaultString: t("common:now_uppercase"),
            });
            break;

          case DUE_ON:
            // Due on - dateTime cell with validation and error state
            columns.push({
              value: dueOn,
              cellType: "dateTime",
              isEditable: true,
              placeholder: label,
              hasError: isDueDateBeforeOpenDate,
              isDisabled: !doesStaffHaveAccessToCourse,
              tooltip: getDueDateTooltip({ isDueDateBeforeOpenDate, t }),
              pickerProps: {
                type: "dateTime",
                showIcon: true,
                initialTime:
                  selectedTimeSlotMoment ||
                  dueOnPreferenceMoment ||
                  dueTimePreferenceMoment ||
                  initialTimeMoment,
                minDate: dueDateRange.minDate,
                maxDate: dueDateRange.maxDate,
                minTime: dueDateRange.minTime,
                isClearable: true,
              },
            });
            break;

          case CLOSES_ON:
            // Closes on - dateTime cell with validation and error state
            columns.push({
              value: closesOn,
              cellType: "dateTime",
              isEditable: true,
              placeholder: label,
              hasError:
                isCloseDateBeforeDueDate ||
                isCloseDateBeforeOpenDate ||
                isCloseDateBeforeCurrentDate,
              isDisabled:
                !isStudentSubmissionEnabled || !doesStaffHaveAccessToCourse,
              tooltip: getCloseDateTooltip({
                isCloseDateBeforeDueDate,
                isCloseDateBeforeCurrentDate,
                isCloseDateBeforeOpenDate,
                t,
              }),
              pickerProps: {
                type: "dateTime",
                showIcon: true,
                initialTime:
                  selectedTimeSlotMoment ||
                  closesOnPreferenceMoment ||
                  initialTimeMoment,
                minDate: closeDateRange.minDate,
                maxDate: closeDateRange.maxDate,
                minTime: closeDateRange.minTime,
                isClearable: true,
              },
            });
            break;

          case VISIBILITY:
            // Visibility - dropdown cell
            columns.push({
              value: [visiblityValue],
              cellType: "dropdown",
              isEditable: true,
              isMulti: false,
              placeholder: label,
              validInputs: {
                options: visibilityOptions,
              },
              isDisabled: !doesStaffHaveAccessToCourse,
            });
            break;

          case TAGS:
            // Tags - tag cell
            columns.push({
              value: _.get(courseSelectedFoldersMap, id, []),
              cellType: "tag",
              isEditable: true,
              isMulti: true,
              addNewOptionEnabled: true,
              placeholder: t("common:select_with_label", {
                label: t("common:tags"),
              }),
              validInputs: {
                options: tagOptions,
              },
              isDisabled: !doesStaffHaveAccessToCourse,
              noOptionsText: t("classRoom:type_tag_name_to_create"),
              noOptionsVariant: "inline",
            });
        }
        return columns;
      },
      initialColumns
    );

    return {
      rowId: id,
      columns,
      originalCourse: course,
      isExistingClass,
    };
  });
};

// Helper function to create DataGrid headers
export const createDataGridHeaders = ({
  isFixedStartTime,
  classWiseSettingsToDisplay,
  t,
}) => {
  const totalColumns = _.size(classWiseSettingsToDisplay) + 2;

  const intialHeaders = [
    {
      id: "ACTIONS",
      width: "4%",
      styles: {
        align: "center",
      },
    },
    {
      id: "CLASS",
      title: t("common:class"),
      width: totalColumns < 5 ? "32%" : totalColumns < 6 ? "25%" : "22%",
    },
  ];

  const headers = _.reduce(
    classWiseSettingsToDisplay,
    (headers, { key, label }) => {
      switch (key) {
        case STUDENTS:
          headers.push({
            id: STUDENTS,
            title: label,
            width: totalColumns < 6 ? "34%" : "22%",
          });
          break;

        case SECURITY_MODE:
          headers.push({
            id: SECURITY_MODE,
            title: t("worksheet:security_mode"),
            width: "17%",
          });
          break;

        case DURATION:
          headers.push({
            id: DURATION,
            title: t("common:duration"),
            width: "12%",
          });
          break;

        case OPENS_ON:
          headers.push({
            id: OPENS_ON,
            title: isFixedStartTime
              ? t("worksheet:starts_on")
              : t("common:opens_on"),
            width: "17%",
          });
          break;

        case DUE_ON:
          headers.push({
            id: DUE_ON,
            title: t("classRoom:due_on"),
            width: "17%",
          });
          break;

        case CLOSES_ON:
          headers.push({
            id: CLOSES_ON,
            title: t("common:closes_on"),
            width: "17%",
          });
          break;

        case VISIBILITY:
          headers.push({
            id: VISIBILITY,
            title: t("common:visibility"),
            width: "27%",
          });
          break;

        case TAGS:
          headers.push({
            id: TAGS,
            title: t("common:tags"),
            width: totalColumns < 5 ? "30%" : "20%",
          });
          break;
      }
      return headers;
    },
    intialHeaders
  );

  return headers;
};
